import React, { useEffect } from 'react';
import PageHeader from '../innovher/PageHeader';
import { IMG } from '../../lib/images';
import { CheckCircle2 } from 'lucide-react';
import SEO from '../SEO';

export default function AcceleratorProgramPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const stages = [
    {
      title: 'Stage 1: Foundation & Compliance',
      duration: '0-30 Days',
      items: [
        { name: 'Validation', desc: 'Document due diligence to ensure legal and operational compliance.' },
        { name: 'Expert Connect', desc: 'Sectoral expert meetup for industry insights and business refinement.' },
        { name: 'Share Transfer', desc: 'Advisory share transfer to facilitate smooth equity allocation.' },
        { name: 'Brand Outreach', desc: 'Social media and community communication for visibility and engagement.' },
      ]
    },
    {
      title: 'Stage 2: Vision & Strategy Alignment',
      duration: '30-90 Days',
      items: [
        { name: 'Assessment', desc: 'Startup Uniqueness Index assessment.' },
        { name: 'Exploration & Research', desc: 'Secondary research analysis for market and industry insights.' },
        { name: 'Financial Advisory', desc: 'Understanding the business model and revenue streams.' },
        { name: 'Pitch Deck Development', desc: 'Creation of Stage 1 Pitch Deck for investor presentations.' },
        { name: 'Goal Setting', desc: 'Seed funding by Innovher ranging from ₹5L to ₹20L, based on valuation and equity.' },
        { name: 'Knowledge Sharing', desc: '5 expert sessions on GTM Strategy, Financial Planning, Business Modeling, Presentation Skills, and Scaling.' },
      ]
    },
    {
      title: 'Stage 3: Capacity Building & Growth',
      duration: '45-200 Days',
      items: [
        { name: 'Capacity Building', desc: 'Sectoral & stage-specific mentorship with two dedicated mentor connections.' },
        { name: 'Ecosystem Exposure', desc: 'Exposure to sector-specific startup ecosystems. Participation in 2 national-level and 2 regional-level events.' },
        { name: 'Funding & Investment', desc: 'Comprehensive investment readiness support.' },
        { name: 'Sales & Collaboration', desc: 'Strategic collaborations for sales growth aligned with the business model.' },
        { name: 'GTM Strategies', desc: 'Brand development and target audience establishment for precise market positioning.' },
      ]
    },
    {
      title: 'Stage 4: Scale & Expansion',
      duration: '150-300 Days',
      items: [
        { name: 'Outreach & Brand Establishment', desc: 'Creation of 2 reels for brand awareness and 2 PR features in national-level publications.' },
        { name: 'Investment & Funding', desc: 'Term sheets and strategic investments of up to ₹1 crore.' },
        { name: 'Financial Advisory', desc: 'MIS reports and a detailed 3-year cash flow statement.' },
        { name: 'Growth Strategy', desc: 'Advisory on startup scalability at national and global levels.' },
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Accelerator Program | InnovACE"
        description="InnovACE, InnovHer's structured accelerator pathway from MVP to scale: validation, mentorship, financial readiness, brand, market positioning and investor connections."
        keywords="InnovACE, InnovHer accelerator, startup acceleration Jaipur, venture growth India, portfolio acceleration"
      />
      <PageHeader
        eyebrow="Initiatives"
        title={<><span className="text-[#ff8ec5]">Accelerator Program</span></>}
        subtitle="Our flagship program provides startups with a structured roadmap, guiding them from MVP to scaling by focusing on key areas such as validation, innovation, financial planning, regulatory compliance, brand development and market positioning."
        image={IMG.accelerator9Mo}
        breadcrumbs={[{ label: 'Initiatives', to: '#' }, { label: 'Accelerator Program' }]}
      />

      <section className="relative bg-[#08080a] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">Structured for Scale</h2>
            <p className="text-lg text-white/60 leading-relaxed">
              The program offers stage-specific mentorship, sector-specific guidance, and investor connections to ensure startups are financially ready, equipped with effective go-to-market strategies, and prepared for scale-up and growth.
            </p>
          </div>

          <div className="space-y-12 lg:space-y-24 relative before:absolute before:inset-0 before:ml-4 lg:before:mx-auto lg:before:translate-x-0 before:w-px before:bg-gradient-to-b before:from-[#d42b92] before:via-[#ff5fb1] before:to-transparent">
            {stages.map((stage, i) => (
              <div key={i} className="reveal-up relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-revealed">
                
                <div className="flex items-center justify-center w-8 h-8 rounded-full border-4 border-[#08080a] bg-[#d42b92] shadow shadow-[#d42b92] absolute left-0 lg:left-1/2 -translate-x-1/2 z-10">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>

                <div className="w-[calc(100%-3rem)] lg:w-[calc(50%-3rem)] ml-12 lg:ml-0 p-6 md:p-8 rounded-3xl bg-white/[0.02] border border-white/10 hover:border-[#d42b92]/50 transition-colors">
                  <div className="text-[#ff8ec5] text-sm font-semibold tracking-wider uppercase mb-2">Duration: {stage.duration}</div>
                  <h3 className="text-2xl font-bold text-white mb-6">{stage.title}</h3>
                  <ul className="space-y-4">
                    {stage.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-white/70">
                        <CheckCircle2 className="w-5 h-5 text-[#d42b92] shrink-0 mt-0.5" />
                        <div>
                          <span className="text-white font-medium mr-2">{item.name}:</span>
                          {item.desc}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
